import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  Tooltip,
  MenuItem,
  Checkbox,
  Grid,
  Divider,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Pending as PendingIcon,
  Payment as PaidIcon,
  Error as FailedIcon,
  ReceiptLong as PayrollIcon,
} from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import {
  fetchMonthlyPayrollReport,
  processMonthlyPayroll,
} from "store/features/company/payrollManagement/processPayroll/processPayrollActions";
import {
  selectPayrollLoading,
  selectPayrollTransactions,
  selectPayrollSummary,
  selectProcessInfo,
} from "store/features/company/payrollManagement/processPayroll/processPayrollSelectors";
import { fetchSalaryConfigurations } from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationActions";
import { selectSalaryConfigurations } from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationSelectors";
import DownloadPayslipModal from "./DownloadPayslipModal";
import processPayrollSvg from "assets/folders/payroll.svg";
import usePermissions from "hooks/usePermissions";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const ProcessPayroll = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const loading = useSelector(selectPayrollLoading);
  const transactions = useSelector(selectPayrollTransactions);
  const summary = useSelector(selectPayrollSummary);
  const processInfo = useSelector(selectProcessInfo);
  const salaryConfigurations = useSelector(selectSalaryConfigurations);

  const [searchTerm, setSearchTerm] = useState("");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { hasPermission } = usePermissions();

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    dispatch(fetchMonthlyPayrollReport({ month, year }));
    dispatch(fetchSalaryConfigurations());
  }, [dispatch, month, year]);

  useEffect(() => {
    setSelectedEmployees([]);
    setPage(0);
  }, [month, year, searchTerm, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getConfigIdByEmployeeId = (employeeId) => {
    if (!employeeId || !salaryConfigurations.length) return null;
    const config = salaryConfigurations.find(
      (c) => c.employee_id === employeeId && c.status === "active",
    );
    return config ? config.id : null;
  };

  const getEmployeeJoiningDate = (employeeId) => {
    if (!employeeId || !salaryConfigurations.length) return null;
    const config = salaryConfigurations.find(
      (c) => c.employee_id === employeeId,
    );
    return config?.joining_date || null;
  };

  const filteredTransactions = transactions.filter((transaction) => {
    const matchesSearch =
      searchTerm === "" ||
      transaction.employee_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.employee_code
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    let matchesStatus = false;
    if (statusFilter === "all") {
      matchesStatus = true;
    } else if (statusFilter === "pending") {
      matchesStatus =
        transaction.payroll_status === null ||
        transaction.payroll_status === "pending";
    } else {
      matchesStatus = transaction.payroll_status === statusFilter;
    }

    // ✅ JOINING DATE FILTER
    let matchesJoiningDate = true;
    const joiningDateStr = getEmployeeJoiningDate(transaction.employee_id);
    if (joiningDateStr) {
      const joiningDate = new Date(joiningDateStr);
      const selectedMonth = month - 1;
      const selectedYear = year;

      if (
        joiningDate.getFullYear() > selectedYear ||
        (joiningDate.getFullYear() === selectedYear &&
          joiningDate.getMonth() > selectedMonth)
      ) {
        matchesJoiningDate = false;
      }
    }

    return matchesSearch && matchesStatus && matchesJoiningDate;
  });

  const selectableEmployees = filteredTransactions.filter((t) => {
    const configId = getConfigIdByEmployeeId(t.employee_id);

    // ✅ JOINING DATE CHECK
    let isEligible = true;
    const joiningDateStr = getEmployeeJoiningDate(t.employee_id);
    if (joiningDateStr) {
      const joiningDate = new Date(joiningDateStr);
      const selectedMonth = month - 1;
      const selectedYear = year;

      if (
        joiningDate.getFullYear() > selectedYear ||
        (joiningDate.getFullYear() === selectedYear &&
          joiningDate.getMonth() > selectedMonth)
      ) {
        isEligible = false;
      }
    }

    return configId && t.payroll_status !== "paid" && isEligible;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const datePart = dateString.includes("T")
        ? dateString.split("T")[0]
        : dateString;
      if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = datePart.split("-");
        // Add 1 to the day
        const incrementedDay = parseInt(day) + 1;
        return `${incrementedDay}/${parseInt(month)}/${year.slice(-2)}`;
      }
      return dateString;
    } catch (e) {
      return "-";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const getPaymentStatusChip = (status) => {
    switch (status) {
      case "success":
        return (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label="Success"
            size="small"
            sx={{
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontWeight: 500,
              fontSize: "0.7rem",
              height: "20px",
            }}
          />
        );
      case "failed":
        return (
          <Chip
            icon={<FailedIcon sx={{ fontSize: 14 }} />}
            label="Failed"
            size="small"
            sx={{
              backgroundColor: "#fee2e2",
              color: "#991b1b",
              fontWeight: 500,
              fontSize: "0.7rem",
              height: "20px",
            }}
          />
        );
      default:
        return null;
    }
  };

  const getProcessStatusChip = (status) => {
    switch (status) {
      case "completed":
        return (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label="Completed"
            size="small"
            sx={{
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      case "processing":
        return (
          <Chip
            icon={<PendingIcon sx={{ fontSize: 14 }} />}
            label="Processing"
            size="small"
            sx={{
              backgroundColor: "#fef3c7",
              color: "#92400e",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      default:
        return null;
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = selectableEmployees
        .map((t) => getConfigIdByEmployeeId(t.employee_id))
        .filter((id) => id !== null);
      setSelectedEmployees(allIds);
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleSelectEmployee = (employeeId) => {
    const configId = getConfigIdByEmployeeId(employeeId);
    if (!configId) return;

    if (selectedEmployees.includes(configId)) {
      setSelectedEmployees(selectedEmployees.filter((id) => id !== configId));
    } else {
      setSelectedEmployees([...selectedEmployees, configId]);
    }
  };

  const handleConfirmDisbursement = () => {
    if (selectedEmployees.length === 0) {
      showErrorToast("Please select at least one employee");
      return;
    }

    // Validate that selected employees have bank details configured
    const selectedWithMissingBank = filteredTransactions.filter((t) => {
      const configId = getConfigIdByEmployeeId(t.employee_id);
      return (
        configId &&
        selectedEmployees.includes(configId) &&
        (!t.bank_name || !t.bank_account_number)
      );
    });

    if (selectedWithMissingBank.length > 0) {
      const names = selectedWithMissingBank
        .map((t) => t.employee_name)
        .join(", ");
      showErrorToast(
        `Cannot process payroll. Bank details are not configured for: ${names}`,
      );
      return;
    }

    setConfirmDialogOpen(true);
  };

  const handleProcessPayroll = async () => {
    setConfirmDialogOpen(false);
    setProcessing(true);
    try {
      const payrollData = {
        month: month,
        year: year,
        employee_salary_configuration_ids: selectedEmployees,
      };

      const result = await dispatch(processMonthlyPayroll(payrollData));
      if (result.success) {
        const data = result.data;
        if (data) {
          if (data.failed_count > 0 && data.errors && data.errors.length > 0) {
            data.errors.forEach((error) => {
              showErrorToast(`❌ ${error}`);
            });
          }
        }
        await dispatch(fetchMonthlyPayrollReport({ month, year }));
        setSelectedEmployees([]);
      }
    } catch (error) {
      console.error("Process payroll error:", error);
      showErrorToast("Failed to process payroll");
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelDisbursement = () => {
    setConfirmDialogOpen(false);
  };

  const handleViewPayslip = (transaction) => {
    setSelectedTransaction(transaction);
    setShowPayslipModal(true);
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const availableMonths = months.map((m, idx) => ({
    name: m,
    value: idx + 1,
    // disabled: idx + 1 > currentMonth && year === currentYear,
    //future month seletion disabled
  }));

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  // Calculate max value for stacked bar
  const getMaxEarningValue = (transaction) => {
    const values = [
      parseFloat(transaction.basic) || 0,
      parseFloat(transaction.hr_allowance) || 0,
      parseFloat(transaction.special_allowance) || 0,
      parseFloat(transaction.conveyance_allowance) || 0,
      parseFloat(transaction.mobile_allowance) || 0,
      parseFloat(transaction.other_allowance) || 0,
      parseFloat(transaction.da) || 0,
      parseFloat(transaction.incentive) || 0,
      parseFloat(transaction.travel_allowance) || 0,
      parseFloat(transaction.bonus) || 0,
      parseFloat(transaction.mediclaim) || 0,
    ];
    return Math.max(...values, 1);
  };

  const getEarningItems = (transaction) => {
    const items = [
      {
        label: "Basic",
        value: parseFloat(transaction.basic) || 0,
        color: "#10b981",
      },
      {
        label: "HRA",
        value: parseFloat(transaction.hr_allowance) || 0,
        color: "#34d399",
      },
      {
        label: "Special",
        value: parseFloat(transaction.special_allowance) || 0,
        color: "#6ee7b7",
      },
      {
        label: "Conveyance",
        value: parseFloat(transaction.conveyance_allowance) || 0,
        color: "#a7f3d0",
      },
      {
        label: "Mobile",
        value: parseFloat(transaction.mobile_allowance) || 0,
        color: "#d1fae5",
      },
      {
        label: "Other",
        value: parseFloat(transaction.other_allowance) || 0,
        color: "#ecfdf5",
      },
      { label: "DA", value: parseFloat(transaction.da) || 0, color: "#fcd34d" },
      {
        label: "Incentive",
        value: parseFloat(transaction.incentive) || 0,
        color: "#fbbf24",
      },
      {
        label: "Travel",
        value: parseFloat(transaction.travel_allowance) || 0,
        color: "primary.main",
      },
      {
        label: "Bonus",
        value: parseFloat(transaction.bonus) || 0,
        color: "#f97316",
      },
      {
        label: "Mediclaim",
        value: parseFloat(transaction.mediclaim) || 0,
        color: "#fb923c",
      },
    ];
    return items.filter((item) => item.value > 0);
  };

  const getDeductionItems = (transaction) => {
    const items = [];
    const basic = parseFloat(transaction.basic) || 0;
    const grossSalary = parseFloat(transaction.gross_salary) || 0;

    const tds = parseFloat(transaction.tds) || 0;
    if (tds > 0) items.push({ label: "TDS", value: tds, color: "#ee2222" });

    if (transaction.pf_applicable === 1) {
      let pfVal = 0;
      if (transaction.pf_type === "number") {
        pfVal = parseFloat(transaction.pf_amount) || 0;
      } else {
        const pfPercentage =
          parseFloat(transaction.pf_percentage) !== undefined &&
          parseFloat(transaction.pf_percentage) !== null &&
          !isNaN(parseFloat(transaction.pf_percentage))
            ? parseFloat(transaction.pf_percentage)
            : 12;
        pfVal = (basic * pfPercentage) / 100;
      }
      items.push({
        label: "PF",
        value: pfVal,
        color: "#f75555",
      });
    }

    if (transaction.esi_applicable === 1) {
      let esiVal = 0;
      if (transaction.esi_type === "number") {
        esiVal = parseFloat(transaction.esi_amount) || 0;
      } else {
        const esiPercentage =
          parseFloat(transaction.esi_percentage) !== undefined &&
          parseFloat(transaction.esi_percentage) !== null &&
          !isNaN(parseFloat(transaction.esi_percentage))
            ? parseFloat(transaction.esi_percentage)
            : 0.75;
        esiVal = (grossSalary * esiPercentage) / 100;
      }
      items.push({
        label: "ESI",
        value: esiVal,
        color: "#e63434",
      });
    }

    if (transaction.pt_applicable === 1) {
      let ptVal = 0;
      if (transaction.pt_type === "number") {
        ptVal =
          parseFloat(transaction.pt_amount) !== undefined &&
          parseFloat(transaction.pt_amount) !== null &&
          !isNaN(parseFloat(transaction.pt_amount))
            ? parseFloat(transaction.pt_amount)
            : 200;
      } else {
        const ptPercentage = parseFloat(transaction.pt_percentage) || 0;
        ptVal = (grossSalary * ptPercentage) / 100;
      }
      items.push({ label: "PT", value: ptVal, color: "#f3224f" });
    }

    return items.filter((item) => item.value > 0);
  };

  if (!hasPermission("process_payroll.view")) {
    return null;
  }

  const getSelectedEmployeeNames = () => {
    const names = filteredTransactions
      .filter((t) => {
        const configId = getConfigIdByEmployeeId(t.employee_id);
        return selectedEmployees.includes(configId);
      })
      .map((t) => t.employee_name);
    return names.join(", ");
  };

  const exportPayrollToExcel = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Yellow header style
    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 12,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFFF00" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    // Title
    XLSX.utils.sheet_add_aoa(
      ws,
      [[`PAYROLL REPORT - ${months[month - 1]} ${year}`]],
      {
        origin: `A${row + 1}`,
      },
    );
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 20 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // Summary
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Employees", transactions.length],
        [
          "Total Net Salary",
          summary?.total_net_salary
            ? formatCurrency(summary.total_net_salary)
            : "₹0",
        ],
        [
          "Total Deductions",
          summary?.total_deductions
            ? formatCurrency(summary.total_deductions)
            : "₹0",
        ],
        [
          "Total Gross Salary",
          summary?.total_gross_salary
            ? formatCurrency(summary.total_gross_salary)
            : "₹0",
        ],
        [
          "Paid Employees",
          transactions.filter((t) => t.payroll_status === "paid").length,
        ],
        [
          "Pending Employees",
          transactions.filter(
            (t) => t.payroll_status === "pending" || !t.payroll_status,
          ).length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 8;

    // Headers
    const headers = [
      "Employee Name",
      "Employee Code",
      "Employee ID",
      "Bank Name",
      "Account Number",
      "IFSC Code",
      "Basic",
      "HRA",
      "DA",
      "Special Allowance",
      "Conveyance",
      "Mobile",
      "Other Allowance",
      "Incentive",
      "Travel Allowance",
      "Bonus",
      "Mediclaim",
      "TDS",
      "PF",
      "ESI",
      "PT",
      "Gross Salary",
      "Net Salary",
      "Payroll Status",
      "Payment Status",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    // Apply yellow header style to header row
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // Data
    filteredTransactions.forEach((transaction) => {
      const basic = parseFloat(transaction.basic) || 0;
      const grossSalary = parseFloat(transaction.gross_salary) || 0;

      // Calculate PF
      let pfValue = 0;
      if (transaction.pf_applicable === 1) {
        if (transaction.pf_type === "number") {
          pfValue = parseFloat(transaction.pf_amount) || 0;
        } else {
          const pfPercentage = parseFloat(transaction.pf_percentage) || 12;
          pfValue = (basic * pfPercentage) / 100;
        }
      }

      // Calculate ESI
      let esiValue = 0;
      if (transaction.esi_applicable === 1) {
        if (transaction.esi_type === "number") {
          esiValue = parseFloat(transaction.esi_amount) || 0;
        } else {
          const esiPercentage = parseFloat(transaction.esi_percentage) || 0.75;
          esiValue = (grossSalary * esiPercentage) / 100;
        }
      }

      // Calculate PT
      let ptValue = 0;
      if (transaction.pt_applicable === 1) {
        if (transaction.pt_type === "number") {
          ptValue = parseFloat(transaction.pt_amount) || 200;
        } else {
          const ptPercentage = parseFloat(transaction.pt_percentage) || 0;
          ptValue = (grossSalary * ptPercentage) / 100;
        }
      }

      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            transaction.employee_name || "",
            transaction.employee_code || "",
            transaction.employee_id || "",
            transaction.bank_name || "",
            transaction.bank_account_number || "",
            transaction.ifsc_code || "",
            formatCurrency(transaction.basic || 0),
            formatCurrency(transaction.hr_allowance || 0),
            formatCurrency(transaction.da || 0),
            formatCurrency(transaction.special_allowance || 0),
            formatCurrency(transaction.conveyance_allowance || 0),
            formatCurrency(transaction.mobile_allowance || 0),
            formatCurrency(transaction.other_allowance || 0),
            formatCurrency(transaction.incentive || 0),
            formatCurrency(transaction.travel_allowance || 0),
            formatCurrency(transaction.bonus || 0),
            formatCurrency(transaction.mediclaim || 0),
            formatCurrency(transaction.tds || 0),
            formatCurrency(pfValue),
            formatCurrency(esiValue),
            formatCurrency(ptValue),
            formatCurrency(
              transaction.gross_salary ||
                transaction.configured_gross_salary ||
                0,
            ),
            formatCurrency(
              transaction.net_salary || transaction.configured_net_salary || 0,
            ),
            transaction.payroll_status || "pending",
            transaction.payment_status || "",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    // Column widths
    ws["!cols"] = headers.map((_, index) => {
      const widths = {
        0: 25, // Employee Name
        1: 18, // Employee Code
        2: 18, // Employee ID
        3: 22, // Bank Name
        4: 22, // Account Number
        5: 18, // IFSC Code
        6: 16, // Basic
        7: 14, // HRA
        8: 14, // DA
        9: 20, // Special Allowance
        10: 18, // Conveyance
        11: 16, // Mobile
        12: 20, // Other Allowance
        13: 18, // Incentive
        14: 20, // Travel Allowance
        15: 16, // Bonus
        16: 18, // Mediclaim
        17: 14, // TDS
        18: 14, // PF
        19: 14, // ESI
        20: 14, // PT
        21: 18, // Gross Salary
        22: 18, // Net Salary
        23: 18, // Payroll Status
        24: 18, // Payment Status
      };
      return { wch: widths[index] || 18 };
    });

    XLSX.utils.book_append_sheet(wb, ws, "Payroll Report");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `Payroll_Report_${months[month - 1]}_${year}.xlsx`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={processPayrollSvg}
              alt="Process Payroll"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Process Payroll
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage monthly payroll processing and employee salary
                disbursement
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            {/* Export Excel Button */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportPayrollToExcel}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Excel
            </Button>

            {hasPermission("process_payroll.create") &&
              selectedEmployees.length > 0 && (
                <Button
                  variant="contained"
                  onClick={handleConfirmDisbursement}
                  disabled={processing}
                  sx={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    borderRadius: "10px",
                    px: 2.5,
                    height: "40px",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    textTransform: "none",
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    boxShadow: "none",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  Confirm Disbursement ({selectedEmployees.length})
                </Button>
              )}
          </Stack>
        </Stack>
      </Box>

      {/* Filters Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2}>
          <TextField
            select
            label="Month"
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            size="small"
            sx={{
              minWidth: 140,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "background.paper",
              },
            }}
          >
            {availableMonths.map((m) => (
              <MenuItem key={m.value} value={m.value} disabled={m.disabled}>
                {m.name} {m.disabled ? "(Future)" : ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Year"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            size="small"
            sx={{
              minWidth: 100,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "background.paper",
              },
            }}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <TextField
          placeholder="Search by employee name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={1}>
          {["all", "pending", "paid"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Summary Cards */}
      {summary && processInfo && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "16px",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                }`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                  }}
                >
                  <PayrollIcon sx={{ color: "primary.main" }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Process Status
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {getProcessStatusChip(processInfo.status)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "16px",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                }`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                  }}
                >
                  <MoneyIcon sx={{ color: "#10b981" }} />
                </Box>
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Total Net Salary
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        p: 0.5,
                        borderRadius: "8px",
                        backgroundColor: "rgba(16, 185, 129, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MoneyIcon sx={{ color: "#10b981", fontSize: 16 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatCurrency(summary.total_net_salary)}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "16px",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                }`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                  }}
                >
                  <CancelIcon sx={{ color: "#ef4444" }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Total Deductions
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCurrency(summary.total_deductions)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "16px",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                }`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                  }}
                >
                  <EventIcon sx={{ color: "#3b82f6" }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Total Employees
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {summary.total_employees}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "primary.main" }} />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading payroll transactions...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "auto",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{ width: 40, minWidth: 40 }}
                  >
                    <Checkbox
                      indeterminate={
                        selectedEmployees.length > 0 &&
                        selectedEmployees.length < selectableEmployees.length
                      }
                      checked={
                        selectableEmployees.length > 0 &&
                        selectedEmployees.length === selectableEmployees.length
                      }
                      onChange={handleSelectAll}
                      disabled={
                        processing ||
                        selectableEmployees.length === 0 ||
                        !hasPermission("process_payroll.create")
                      }
                      sx={{
                        color: "primary.main",
                        "&.Mui-checked": {
                          color: "#10b981",
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell
                    sx={{
                      width: 180,
                      minWidth: 180,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Employee
                  </TableCell>

                  <TableCell
                    sx={{
                      width: 150,
                      minWidth: 150,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Bank Details
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 90,
                      minWidth: 80,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Basic
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    HRA
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    DA
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 100,
                      minWidth: 80,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Special
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 110,
                      minWidth: 90,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Conveyance
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 100,
                      minWidth: 80,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Mobile
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Other
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 90,
                      minWidth: 80,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Incentive
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 100,
                      minWidth: 80,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Travel
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Bonus
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 90,
                      minWidth: 80,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#10b981",
                    }}
                  >
                    Mediclaim
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#ef4444",
                    }}
                  >
                    TDS
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#ef4444",
                    }}
                  >
                    ESI
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#ef4444",
                    }}
                  >
                    PF
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#ef4444",
                    }}
                  >
                    PT
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 70,
                      minWidth: 60,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#8952e7",
                    }}
                  >
                    Gross
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 110,
                      minWidth: 100,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      color: "#C49102",
                    }}
                  >
                    Net Salary
                  </TableCell>

                  <TableCell
                    sx={{
                      width: 100,
                      minWidth: 90,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Status
                  </TableCell>

                  {hasPermission("process_payroll.view") && (
                    <TableCell
                      align="center"
                      sx={{
                        width: 60,
                        minWidth: 50,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        fontSize: "0.7rem",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <ReceiptIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography sx={{ color: "#6b7280" }}>
                        No payroll transactions found for {months[month - 1]}{" "}
                        {year}.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((transaction) => {
                      const configId = getConfigIdByEmployeeId(
                        transaction.employee_id,
                      );
                      const isPaid = transaction.payroll_status === "paid";
                      const isSelectable = !!configId && !isPaid;
                      const isSelected = selectedEmployees.includes(configId);
                      const maxEarning = getMaxEarningValue(transaction);
                      const earningItems = getEarningItems(transaction);
                      const deductionItems = getDeductionItems(transaction);

                      return (
                        <TableRow
                          key={transaction.employee_id}
                          component={motion.tr}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          sx={{
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.5)"
                                  : "#f9fafb",
                            },
                            backgroundColor: isSelected
                              ? theme.palette.mode === "dark"
                                ? "rgba(16,185,129,0.08)"
                                : "#ecfdf5"
                              : "transparent",
                            opacity: isPaid ? 0.7 : 1,
                          }}
                        >
                          <TableCell padding="checkbox">
                            {isPaid ? (
                              <Checkbox
                                checked={true}
                                disabled
                                sx={{
                                  color: "#d1d5db",
                                  "&.Mui-checked": {
                                    color: "#d1d5db",
                                  },
                                  "& .MuiSvgIcon-root": {
                                    color: "#d1d5db",
                                  },
                                }}
                              />
                            ) : (
                              <Checkbox
                                checked={isSelected}
                                onChange={() =>
                                  handleSelectEmployee(transaction.employee_id)
                                }
                                disabled={
                                  !isSelectable ||
                                  processing ||
                                  !hasPermission("process_payroll.create")
                                }
                                sx={{
                                  color: "primary.main",
                                  "&.Mui-checked": {
                                    color: "#10b981",
                                  },
                                  "&.Mui-disabled": {
                                    color: "#d1d5db",
                                  },
                                }}
                              />
                            )}
                          </TableCell>

                          {/* Employee Column */}
                          <TableCell>
                            <Stack>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.875rem",
                                  color: "text.primary",
                                }}
                              >
                                {transaction.employee_name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "text.secondary",
                                  fontFamily: "monospace",
                                }}
                              >
                                Config ID: #{configId || "N/A"} | Emp ID:{" "}
                                {transaction.employee_id} | Code:{" "}
                                {transaction.employee_code}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "primary.main",
                                  fontFamily: "monospace",
                                }}
                              >
                                Structure: #{transaction.salary_structure_id}
                              </Typography>
                              {(!transaction.bank_name ||
                                !transaction.bank_account_number) && (
                                <Typography
                                  sx={{
                                    fontSize: "0.65rem",
                                    color: "#ef4444",
                                    fontWeight: 600,
                                    mt: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  ⚠️ Bank Details Missing
                                </Typography>
                              )}
                              {isPaid && (
                                <Chip
                                  label="✅ Disbursed"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#d1fae5",
                                    color: "#065f46",
                                    fontSize: "0.6rem",
                                    height: "18px",
                                    width: "fit-content",
                                    mt: 0.5,
                                  }}
                                />
                              )}
                            </Stack>
                          </TableCell>

                          {/* Bank Details */}
                          <TableCell>
                            {transaction.bank_name ? (
                              <Stack spacing={0.5}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#ffffff"
                                        : "#374151",
                                  }}
                                >
                                  {transaction.bank_name}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "text.secondary",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  A/C: {transaction.bank_account_number || "—"}
                                </Typography>
                                {transaction.ifsc_code && (
                                  <Typography
                                    sx={{
                                      fontSize: "0.65rem",
                                      color: "#9ca3af",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    IFSC: {transaction.ifsc_code}
                                  </Typography>
                                )}
                              </Stack>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color: "#ef4444",
                                  fontStyle: "italic",
                                  fontWeight: 500,
                                }}
                              >
                                Not Configured
                              </Typography>
                            )}
                          </TableCell>

                          {/* Basic Salary */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(transaction.basic || 0)}
                            </Typography>
                          </TableCell>

                          {/* HRA */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.06)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(transaction.hr_allowance || 0)}
                            </Typography>
                          </TableCell>

                          {/* DA */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(transaction.da || 0)}
                            </Typography>
                          </TableCell>

                          {/* Special Allowance */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.06)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(
                                transaction.special_allowance || 0,
                              )}
                            </Typography>
                          </TableCell>

                          {/* Conveyance Allowance */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(
                                transaction.conveyance_allowance || 0,
                              )}
                            </Typography>
                          </TableCell>

                          {/* Mobile Allowance */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.06)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(
                                transaction.mobile_allowance || 0,
                              )}
                            </Typography>
                          </TableCell>

                          {/* Other Allowance */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(transaction.other_allowance || 0)}
                            </Typography>
                          </TableCell>

                          {/* Incentive */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.06)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(transaction.incentive || 0)}
                            </Typography>
                          </TableCell>

                          {/* Travel Allowance */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(
                                transaction.travel_allowance || 0,
                              )}
                            </Typography>
                          </TableCell>

                          {/* Bonus */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.06)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(transaction.bonus || 0)}
                            </Typography>
                          </TableCell>

                          {/* Mediclaim */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(16, 185, 129, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#10b981",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(transaction.mediclaim || 0)}
                            </Typography>
                          </TableCell>

                          {/* TDS */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(244, 63, 94, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#ef4444",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(transaction.tds || 0)}
                            </Typography>
                          </TableCell>

                          {/* ESI */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(220, 38, 38, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#ef4444",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(
                                transaction.esi_applicable === 1
                                  ? parseFloat(transaction.gross_salary) *
                                      0.0075 || 0
                                  : 0,
                              )}
                            </Typography>
                          </TableCell>

                          {/* PF */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(244, 63, 94, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#ef4444",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(
                                transaction.pf_applicable === 1
                                  ? parseFloat(transaction.basic) * 0.12 || 0
                                  : 0,
                              )}
                            </Typography>
                          </TableCell>

                          {/* PT */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(220, 38, 38, 0.08)" }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#ef4444",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(
                                transaction.pt_applicable === 1 ? 200 : 0,
                              )}
                            </Typography>
                          </TableCell>

                          {/* Gross Salary */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(139, 92, 246, 0.08)" }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: "#8952e7",
                                fontSize: "0.9rem",
                              }}
                            >
                              {formatCurrency(
                                transaction.gross_salary ||
                                  transaction.configured_gross_salary,
                              )}
                            </Typography>
                          </TableCell>

                          {/* Net Salary */}
                          <TableCell
                            sx={{ backgroundColor: "rgba(234, 179, 8, 0.08)" }}
                          >
                            <Stack spacing={0.5}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: "#ca8a04",
                                  fontSize: "1.1rem",
                                }}
                              >
                                {formatCurrency(
                                  transaction.net_salary ||
                                    transaction.configured_net_salary,
                                )}
                              </Typography>
                              {transaction.payment_date && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#9ca3af", fontStyle: "italic" }}
                                >
                                  Paid on:{" "}
                                  {formatDate(transaction.payment_date)}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Stack spacing={1}>
                              {/* Payroll Status */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.8,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    fontWeight: 600,
                                    color: "#9ca3af",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    minWidth: "52px",
                                  }}
                                >
                                  Payroll:
                                </Typography>
                                {transaction.payroll_status === "paid" ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <CheckCircleIcon
                                      sx={{ fontSize: 14, color: "#10b981" }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        color: "#10b981",
                                      }}
                                    >
                                      Paid
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "primary.main",
                                    }}
                                  >
                                    {transaction.payroll_status || "Pending"}
                                  </Typography>
                                )}
                              </Box>

                              {/* Payment Status */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.8,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    fontWeight: 600,
                                    color: "#9ca3af",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    minWidth: "52px",
                                  }}
                                >
                                  Payment:
                                </Typography>
                                {transaction.payment_status === "success" ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <CheckCircleIcon
                                      sx={{ fontSize: 14, color: "#10b981" }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        color: "#10b981",
                                      }}
                                    >
                                      Success
                                    </Typography>
                                  </Box>
                                ) : transaction.payment_status === "failed" ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <CancelIcon
                                      sx={{ fontSize: 14, color: "#ef4444" }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        color: "#ef4444",
                                      }}
                                    >
                                      Failed
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "#9ca3af",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    -
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Actions */}
                          {hasPermission("process_payroll.view") && (
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  isPaid
                                    ? "View Payslip"
                                    : "Payslip not available"
                                }
                              >
                                <Box
                                  sx={{
                                    position: "relative",
                                    display: "inline-flex",
                                  }}
                                >
                                  <IconButton
                                    onClick={() =>
                                      handleViewPayslip(transaction)
                                    }
                                    size="small"
                                    sx={{
                                      color: isPaid ? "#3b82f6" : "#9ca3af",
                                      "&:hover": {
                                        backgroundColor: isPaid
                                          ? theme.palette.mode === "dark"
                                            ? "rgba(59,130,246,0.2)"
                                            : "#eff6ff"
                                          : "transparent",
                                      },
                                      cursor: isPaid ? "pointer" : "default",
                                    }}
                                    disabled={!isPaid}
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                  {!isPaid && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform:
                                          "translate(-50%, -50%) rotate(-45deg)",
                                        width: "20px",
                                        height: "2px",
                                        backgroundColor: "#9ca3af",
                                        borderRadius: "1px",
                                        pointerEvents: "none",
                                      }}
                                    />
                                  )}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Count Summary */}
      {!loading && transactions.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">
            Total Transactions: {transactions.length}
          </Typography>
          <Typography variant="body2">
            Selected: {selectedEmployees.length} employees
          </Typography>
        </Stack>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDisbursement}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b2e 100%)"
                : "#ffffff",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(0, 0, 0, 0.8)"
                  : "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleCancelDisbursement}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#10b981", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Confirm Salary Disbursement
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to disburse salary for
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#10b981",
              textAlign: "center",
              mb: 2,
            }}
          >
            {selectedEmployees.length} employee
            {selectedEmployees.length > 1 ? "s" : ""}
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "0.875rem",
              color: "#6b7280",
              mb: 2,
            }}
          >
            {getSelectedEmployeeNames()}
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "0.8rem",
              color: "#ef4444",
              mb: 2,
            }}
          >
            ⚠️ This action cannot be undone.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleCancelDisbursement}
              disabled={processing}
              fullWidth
              variant="outlined"
              sx={{ borderRadius: "12px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProcessPayroll}
              disabled={processing}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                borderRadius: "12px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                },
              }}
            >
              {processing ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                `Confirm Disbursement (${selectedEmployees.length})`
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Download Payslip Modal */}
      <DownloadPayslipModal
        isOpen={showPayslipModal}
        onClose={() => {
          setShowPayslipModal(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
      />
    </Box>
  );
};

export default ProcessPayroll;
